// @flow

import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { TASK_INSTANCE_COMPLETED_STATUS } from 'sling-calendar-sdk/constants';
import styled from 'styled-components';

import type {
  TaskInstance,
  User,
  ShowWarningBarActionCreator,
  HideWarningBarActionCreator,
  CompleteTaskInstanceActionCreator,
  ReopenTaskInstanceActionCreator
} from '../common/typings';
import AnimatedRow from '../common/components/Forms/AnimatedRow';
import ButtonContainer from '../common/components/Forms/ButtonContainer';
import Button from '../common/components/Button';
import RowIcon from '../common/components/Forms/RowIcon';
import RowContent from '../common/components/Forms/RowContent';
import FormContainer from '../common/components/Forms/FormContainer';
import FormScreenContainer from '../common/components/ui/FormScreenContainer';
import FormFieldsContainer from '../common/components/ui/FormFieldsContainer';
import PaddedContainer from '../common/components/ui/PaddedContainer';
import Label from '../common/components/Text/Label';
import ContentText from '../common/components/Text/ContentText';
import Subtask from '../shiftTasks/Subtask';
import { showWarningBar, hideWarningBar } from '../core/actions';
import taskIcon from '../assets/svg/icons/task-gray.svg';
import notesIcon from '../assets/svg/icons/notes-gray.svg';
import subtasksIcon from '../assets/svg/icons/checklist-gray.svg';

import { completeTaskInstance, reopenTaskInstance } from './actions';

const ExtendedRowContent = styled(RowContent)`
  margin-left: 10px;
`;

const PaddedLabel = styled.div`
  padding-left: 10px;
`;

type Props = {
  deviceHeight: number,
  taskInstance: TaskInstance,
  user: User | null,
  onBack: () => void,
  showWarningBar: ShowWarningBarActionCreator,
  hideWarningBar: HideWarningBarActionCreator,
  completeTaskInstance: CompleteTaskInstanceActionCreator,
  reopenTaskInstance: ReopenTaskInstanceActionCreator,
  onViewActivated: (ref: Object) => void
};

export function SingleTaskFieldsComponent({
  deviceHeight,
  taskInstance,
  user,
  onBack,
  showWarningBar,
  hideWarningBar,
  completeTaskInstance,
  reopenTaskInstance,
  onViewActivated
}: Props) {
  const { title, description, subtasks, status } = taskInstance;
  const isCompleted = status === TASK_INSTANCE_COMPLETED_STATUS;
  const {
    titleAnimationDelay,
    descriptionAnimationDelay,
    subtasksAnimationDelay,
    buttonsAnimationDelay
  } = getAnimationDelays(taskInstance);

  const ref = useRef();

  useEffect(() => {
    onViewActivated(ref);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let timeoutId: TimeoutID;
    if (isCompleted && subtasks?.length) {
      timeoutId = setTimeout(() => {
        showWarningBar("To update a subtask's status, reopen a task.");
      }, 1000);
    }
    return () => {
      clearTimeout(timeoutId);
      hideWarningBar();
    };
  }, [isCompleted, subtasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const complete = () => {
    completeTaskInstance(taskInstance);
  };

  const reopen = () => {
    reopenTaskInstance(taskInstance);
  };

  return (
    <FormScreenContainer ref={ref}>
      <FormContainer>
        <FormFieldsContainer deviceHeight={deviceHeight}>
          <PaddedContainer>
            <AnimatedRow borderTop borderBottom delay={titleAnimationDelay}>
              <RowIcon src={taskIcon} />
              <RowContent>
                <Label>Title</Label>
                <ContentText>{title}</ContentText>
              </RowContent>
            </AnimatedRow>

            {Boolean(description && description.length) && (
              <AnimatedRow borderBottom delay={descriptionAnimationDelay}>
                <RowIcon src={notesIcon} />
                <RowContent>
                  <Label>Description</Label>
                  <ContentText>{description}</ContentText>
                </RowContent>
              </AnimatedRow>
            )}

            {Boolean(subtasks && subtasks.length) && (
              <AnimatedRow borderBottom delay={subtasksAnimationDelay}>
                <RowIcon src={subtasksIcon} />
                <ExtendedRowContent>
                  <PaddedLabel>
                    <Label>Subtasks</Label>
                  </PaddedLabel>
                  {subtasks?.map(subtask => (
                    <Subtask
                      key={subtask.id}
                      subtask={subtask}
                      taskInstance={taskInstance}
                      deviceHeight={deviceHeight}
                      user={user}
                    />
                  ))}
                </ExtendedRowContent>
              </AnimatedRow>
            )}
          </PaddedContainer>
        </FormFieldsContainer>
        <ButtonContainer delay={buttonsAnimationDelay}>
          {!isCompleted && (
            <Button
              label="Complete task"
              buttonTheme="grey"
              themeColor="btnGreenTextColor"
              onPress={complete}
            />
          )}
          {isCompleted && (
            <Button label="Reopen task" buttonTheme="grey" themeColor="btnBrand" onPress={reopen} />
          )}
          <Button label="Back" buttonTheme="grey" onPress={onBack} withMargin />
        </ButtonContainer>
      </FormContainer>
    </FormScreenContainer>
  );
}

function getAnimationDelays(taskInstance: TaskInstance) {
  const { description, subtasks } = taskInstance;

  const titleAnimationDelay = 0.2;
  let lastAnimationDelay = 0.2;

  let descriptionAnimationDelay;
  if (description) {
    lastAnimationDelay = descriptionAnimationDelay = lastAnimationDelay + 0.2;
  }

  let subtasksAnimationDelay;
  if (subtasks?.length) {
    lastAnimationDelay = subtasksAnimationDelay = lastAnimationDelay + 0.2;
  }

  const buttonsAnimationDelay = lastAnimationDelay + 0.2;

  return {
    titleAnimationDelay,
    descriptionAnimationDelay,
    subtasksAnimationDelay,
    buttonsAnimationDelay
  };
}

export default connect(null, {
  showWarningBar,
  hideWarningBar,
  completeTaskInstance,
  reopenTaskInstance
})(SingleTaskFieldsComponent);
